import { z } from 'zod';

// Wire shapes for the analytics router. Used by the API server (output type)
// and by the frontend chart components (input type).

export const OddsTickPointSchema = z.object({
  observedAt: z.string().datetime(), // ISO8601 UTC
  book: z.string(),
  oddsP1: z.number().positive(),
  oddsP2: z.number().positive(),
  isLive: z.boolean(),
});
export type OddsTickPoint = z.infer<typeof OddsTickPointSchema>;

// Directional move detected over a sliding window. V2 supports BOTH
// directions (drop = odds went down, rise = odds went up) — operators
// care about both: pick dropping is favorable, rival rising is favorable.
//
// `reversion` (0..1) reports how much the move has bounced back from
// the extreme. The detector suppresses moves with reversion ≥ 0.5 by
// default, but the value is reported anyway so the UI can show
// "drop −8%, 30% recovered" if needed.
//
// `book` is the source of the signal — V2 runs the detector per book
// and picks Pinnacle when present (sharpest market) or the largest
// |percentChange| otherwise. The operator sees which book moved.
export const OddsMoveSchema = z.object({
  direction: z.enum(['drop', 'rise']),
  fromOdds: z.number().positive(),
  toOdds: z.number().positive(),
  /** Most recent odds — what the operator is currently looking at. */
  currentOdds: z.number().positive(),
  /** Signed: negative for drop, positive for rise. */
  percentChange: z.number(),
  /** ISO of the start of the move (peak for drop, trough for rise). */
  since: z.string().datetime(),
  /** ISO of the most recent tick — drives the freshness indicator. */
  asOf: z.string().datetime(),
  /** 0 = current at extreme (move holds); 1 = fully reverted. */
  reversion: z.number().min(0).max(1),
  /** Source book of the signal. Pinnacle preferred. */
  book: z.string(),
});
export type OddsMove = z.infer<typeof OddsMoveSchema>;

export const MatchOddsHistoryInputSchema = z.object({
  matchId: z.string().uuid(),
  market: z.literal('h2h').default('h2h'), // V1: H2H only
  // Window — last N minutes. Default 4h, max 12h.
  sinceMinutes: z.number().int().min(15).max(720).default(240),
  // Optional: filter to specific books. Omitting = all books that the
  // connector emitted for this match in the window.
  books: z.array(z.string()).max(20).optional(),
});
export type MatchOddsHistoryInput = z.infer<typeof MatchOddsHistoryInputSchema>;

export const MatchOddsHistoryOutputSchema = z.object({
  matchId: z.string().uuid(),
  market: z.literal('h2h'),
  ticks: z.array(OddsTickPointSchema),
  // Distinct books present in `ticks` — convenience for the UI to render
  // a book selector without scanning the array client-side.
  books: z.array(z.string()),
  // Per-side directional move, computed server-side per book with
  // Pinnacle priority. null = no qualifying move (flat / below threshold
  // / already recovered).
  moveP1: OddsMoveSchema.nullable(),
  moveP2: OddsMoveSchema.nullable(),
  // Window the API actually returned. May differ from sinceMinutes input
  // if the result was truncated to fit the cap.
  windowMinutes: z.number().int(),
  // True when the result hit the row limit and the time window may be
  // narrower than requested. UI should warn.
  truncated: z.boolean(),
});
export type MatchOddsHistoryOutput = z.infer<typeof MatchOddsHistoryOutputSchema>;
